import {noDuplicateId, ValueDefinitionSchema} from '@/model/schema/value';
import {RemoteValueDefinition, ValueDefinition} from '@/model/types';
import {getDefinitions} from '@/repositories/beneva-api/endpoints/definitions';
import {
  FetchExternalValueDefinitionError,
  DuplicateValueDefinitionIdError,
  RemoteDefinitionsLeftError,
} from '../errors';

const fetchExternalValueDefinition = async (
  def: Extract<RemoteValueDefinition, {$fetch: unknown}>,
): Promise<ValueDefinition> => {
  const response = await fetch(def.$fetch);

  if (!response.ok) {
    throw new FetchExternalValueDefinitionError(def.$fetch);
  }

  return await response.json();
};

const noMoreRemoteDefinition = (
  definitions: (ValueDefinition | RemoteValueDefinition)[],
): definitions is ValueDefinition[] => {
  return !definitions.some(def => '$remote' in def || '$fetch' in def);
};

export const createValueDomain = async (
  values: (ValueDefinition | RemoteValueDefinition)[],
): Promise<ValueDefinition[]> => {
  const definitions = await fetchRemoteDefinitions(await fetchExternalDefinitions(values));

  if (!noMoreRemoteDefinition(definitions)) {
    throw new RemoteDefinitionsLeftError();
  }

  if (!noDuplicateId.rule(definitions)) {
    throw new DuplicateValueDefinitionIdError();
  }

  return definitions;
};

const fetchRemoteDefinitions = async (
  values: (ValueDefinition | RemoteValueDefinition)[],
): Promise<(ValueDefinition | RemoteValueDefinition)[]> => {
  const definitionsToFetch = values.reduce<{index: number; def: Extract<RemoteValueDefinition, {$remote: string}>}[]>(
    (acc, value, index) => {
      if ('$remote' in value) {
        acc.push({index, def: value});
      }
      return acc;
    },
    [],
  );

  if (definitionsToFetch.length === 0) {
    return values;
  }

  const {data: fetchedDefinitions} = await getDefinitions(definitionsToFetch.map(({def}) => def.$remote));

  const definitions = [...values];

  for (const fetched of fetchedDefinitions) {
    const index = definitionsToFetch.find(({def}) => def.$remote === fetched.id)?.index;
    if (index !== undefined) {
      definitions[index] = fetched;
    }
  }

  return definitions;
};

const fetchExternalDefinitions = async (
  values: (ValueDefinition | RemoteValueDefinition)[],
): Promise<(ValueDefinition | RemoteValueDefinition)[]> => {
  const definitionsToFetch = values.reduce<{index: number; def: Extract<RemoteValueDefinition, {$fetch: string}>}[]>(
    (acc, value, index) => {
      if ('$fetch' in value) {
        acc.push({index, def: value});
      }
      return acc;
    },
    [],
  );

  const fetchedDefinitions = await Promise.all(
    definitionsToFetch.map(async ({index, def}) => {
      const data = await fetchExternalValueDefinition(def);

      return {index, def: ValueDefinitionSchema.parse(data)};
    }),
  );

  const definitions = [...values];

  for (const {index, def} of fetchedDefinitions) {
    definitions[index] = def;
  }

  return definitions;
};
